import { useState } from 'react';
import { Stack, Text } from '@repletoryreach/sdk-ui';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import type { admin } from '@repletoryreach/sdk-api';
import { useUpdateLanguage } from '../../../../data/useLanguages';
import { formatInteger } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { linesInCode, missingLines } from '../languages';

/**
 * Switching a language on or off, always behind a confirmation (UI-CONVENTIONS §5), because both reach every
 * business: on, any business can offer it to its customers; off, no business can, and the chatbots that offer it
 * stop speaking it, so their customers get each chatbot's main language.
 */
export function useLanguageSwitch() {
  const t = useT();
  const update = useUpdateLanguage();
  const [busy, setBusy] = useState<string | null>(null);

  function save(l: admin.AdminLanguage, on: boolean) {
    setBusy(l.code);
    update.mutate({ code: l.code, change: { enabled: on } }, {
      onSuccess: () => notifySuccess(t(on ? 'billing.languages.toast.on' : 'billing.languages.toast.off', { name: l.name }), t('billing.languages.title')),
      onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.languages.toast.failed', { name: l.name })),
      onSettled: () => setBusy(null)
    });
  }

  function confirmOff(l: admin.AdminLanguage) {
    openConfirmModal({
      title: t('billing.languages.off.title', { name: l.name }),
      children: (
        <Stack gap="sm">
          <Text role="bodySmall">{t('billing.languages.off.body', { name: l.name })}</Text>
          {l.bots_offering > 0 && (
            <Text role="bodySmall">{t('billing.languages.off.bots', { count: formatInteger(l.bots_offering) })}</Text>
          )}
        </Stack>
      ),
      labels: { confirm: t('billing.languages.off.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => save(l, false)
    });
  }

  function confirmOn(l: admin.AdminLanguage) {
    const missing = linesInCode(l.code) ? 0 : missingLines(l);
    openConfirmModal({
      title: t('billing.languages.on.title', { name: l.name }),
      children: (
        <Stack gap="sm">
          <Text role="bodySmall">{t('billing.languages.on.body', { name: l.name, own: l.native_name })}</Text>
          {l.quality === 'review' && (
            <Text role="bodySmall" c="var(--rr-color-warning)">{t('billing.languages.on.review')}</Text>
          )}
          {missing > 0 && (
            <Text role="bodySmall" c="var(--rr-color-warning)">
              {t('billing.languages.on.missing', { count: formatInteger(missing), total: formatInteger(l.wordings.total) })}
            </Text>
          )}
        </Stack>
      ),
      labels: { confirm: t('billing.languages.on.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => save(l, true)
    });
  }

  return {
    /** The code of the language being saved, so its switch waits. */
    busy,
    toggle: (l: admin.AdminLanguage, on: boolean) => (on ? confirmOn(l) : confirmOff(l))
  };
}
